import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { apiFetch } from '../lib/api';

interface EventItem {
  eventType: string;
  receivedAt: string;
  raw?: string;
}

const EVENT_COLORS: Record<string, string> = {
  Send: 'bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200',
  Delivery: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200',
  Bounce: 'bg-red-50 text-red-700 ring-1 ring-red-200',
  Complaint: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200',
};

function summarizeEventFields(event: EventItem): Array<[string, string]> {
  if (!event.raw) return [];

  try {
    const parsed = JSON.parse(event.raw) as Record<string, unknown>;
    return Object.entries(parsed)
      .filter(([key, value]) => key !== 'eventType' && key !== 'receivedAt' && value !== undefined && value !== null)
      .slice(0, 4)
      .map(([key, value]) => [key, typeof value === 'string' ? value : JSON.stringify(value)]);
  } catch {
    return [];
  }
}

export function EventDetail() {
  const { messageId } = useParams<{ messageId: string }>();
  const eventQuery = useQuery({
    queryKey: ['message-events', messageId],
    queryFn: async () => apiFetch(`/events/by-message/${messageId}`) as Promise<{ items: EventItem[] }>,
    enabled: Boolean(messageId),
  });

  if (!messageId) return null;

  const items = eventQuery.data?.items ?? [];

  return (
    <div className="space-y-6">
      <div>
        <Link
          to="/events"
          className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-zinc-700 transition-colors"
        >
          <svg viewBox="0 0 16 16" fill="currentColor" className="h-3.5 w-3.5">
            <path fillRule="evenodd" d="M7.78 12.53a.75.75 0 01-1.06 0L2.47 8.28a.75.75 0 010-1.06l4.25-4.25a.75.75 0 011.06 1.06L4.81 7h7.44a.75.75 0 010 1.5H4.81l2.97 2.97a.75.75 0 010 1.06z" clipRule="evenodd" />
          </svg>
          Events
        </Link>
        <h1 className="mt-3 text-xl font-semibold text-zinc-900">Message Timeline</h1>
        <p className="mt-0.5 font-mono text-xs text-zinc-400">{messageId}</p>
      </div>

      <div className="relative space-y-3 pl-6 before:absolute before:left-2 before:top-2 before:h-[calc(100%-1rem)] before:w-px before:bg-zinc-200">
        {items.map((event, index) => (
          <div
            key={`${event.eventType}-${event.receivedAt}-${index}`}
            className="relative rounded-xl border border-zinc-200 bg-white p-4 shadow-sm"
          >
            <span
              className="absolute -left-[1.35rem] top-4 flex h-3 w-3 items-center justify-center rounded-full bg-white ring-2 ring-zinc-300"
              aria-hidden="true"
            />
            <div className="flex items-center justify-between gap-3">
              <span
                className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                  EVENT_COLORS[event.eventType] ?? 'bg-zinc-100 text-zinc-600 ring-1 ring-zinc-200'
                }`}
              >
                {event.eventType}
              </span>
              <time className="text-xs tabular-nums text-zinc-400">{event.receivedAt}</time>
            </div>

            {summarizeEventFields(event).length ? (
              <dl className="mt-3 grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
                {summarizeEventFields(event).map(([key, value]) => (
                  <div key={`${event.receivedAt}-${key}`}>
                    <dt className="text-xs font-medium uppercase tracking-wider text-zinc-400">{key}</dt>
                    <dd className="mt-0.5 break-all text-zinc-700">{value}</dd>
                  </div>
                ))}
              </dl>
            ) : null}

            {event.raw ? (
              <details className="mt-3">
                <summary className="cursor-pointer text-xs font-medium text-zinc-500 hover:text-zinc-900 transition-colors">
                  Raw JSON
                </summary>
                <pre className="mt-2 overflow-x-auto rounded-lg bg-zinc-50 p-3 text-xs text-zinc-700">
                  {event.raw}
                </pre>
              </details>
            ) : null}
          </div>
        ))}

        {!items.length && !eventQuery.isLoading ? (
          <p className="text-sm text-zinc-400">No events found for this message.</p>
        ) : null}
      </div>
    </div>
  );
}
